import React from 'react';

import { Text } from '@reckon-web/text';

import { Dialog } from '../components/common/dialog';
import { useSearchJobs } from '../data/useSearchJobs';

import { useDialog } from './use-dialog';

const ConfirmDupliateNameDialog: React.FC<{
  onClose: (ok?: boolean) => void;
  jobName: string;
  debtorName: string;
}> = ({ onClose, jobName, debtorName }) => (
  <Dialog
    title="Use duplicate job name?"
    cancelLabel="No, pick another"
    onCancel={onClose}
    submitLabel="Yes, use this name"
    onSubmit={() => onClose(true)}
  >
    <Text>
      Job name{' '}
      <Text inline weight="semibold">
        {jobName}
      </Text>{' '}
      already exists for{' '}
      <Text inline weight="semibold">
        {debtorName}
      </Text>
      . You can still use the same name, but it might make jobs harder to
      distinguish from one another.
    </Text>
  </Dialog>
);

export const useCheckDupliateJobName = () => {
  // No need to cache these results. We really don't want them cached, we want it to be fresh all the time.
  // If they make a job "foo" for client "bar", we run the dupe check and get nothing back.
  // Then, they make a job "foo" for client "bar", we run the dupe check and get nothing back. This is incorrect
  // Yes, we uncache any job search results on job create. But that just means we'll re-run the last dupe check, which is unnecessary
  // https://www.apollographql.com/docs/react/caching/advanced-topics/#bypassing-the-cache
  const { refetch } = useSearchJobs({ skip: true, fetchPolicy: 'no-cache' });

  const [Dialog, openDialog] = useDialog();

  const checkDupliateJobName = React.useCallback(
    async ({
      jobId,
      debtorId,
      jobName,
      debtorName,
    }: {
      jobId?: string;
      debtorId: string;
      jobName: string;
      debtorName: string;
    }) => {
      if (!refetch) {
        return false;
      }
      const { data } = await refetch({
        clients: [debtorId],
        text: jobName,
        myWork: false,
      });

      const jobs = data?.searchJobs;

      if (!jobs || jobs.length === 0) {
        // happy path
        return true;
      }

      // we have some jobs. Check if we have an exact case insensitive mathc
      // the graphql is doing a LIKE match, so we have to do full name check
      // Just grab all of them, we'll check for this specific job (if we have a job id) in a subsequent check
      // I feel we could give preference to the job they're currently editing: "&& (!jobId || j.cloudId === jobId)"
      // but there's something about that logic that just doesn't sit well
      const exactMatches = jobs.filter(
        (j) =>
          (j.name || '').localeCompare(jobName, undefined, {
            sensitivity: 'base',
          }) === 0
      );

      if (exactMatches.length === 0) {
        // second happy path, actually no exact matches
        return true;
      }

      const hasThisJob =
        !!jobId && exactMatches.some((j) => j.cloudId === jobId);
      if (hasThisJob) {
        // third happy path, an exact match exists but it's for the id passed in
        // ie: updating a job but haven't changed the name
        return true;
      }

      // ruh roh, we have a match. Prompt them
      const dialogResult = await openDialog<boolean>(
        ({ onClose }) => (
          <ConfirmDupliateNameDialog
            onClose={onClose}
            jobName={jobName}
            debtorName={debtorName}
          />
        ),
        {
          size: 'small',
        }
      );

      // and just return whatever the dialog said. It'll be true for overwrite, false for not
      return dialogResult || false;
    },
    [openDialog, refetch]
  );

  return React.useMemo(
    () =>
      [Dialog, checkDupliateJobName] as [
        typeof Dialog,
        typeof checkDupliateJobName,
      ],
    [Dialog, checkDupliateJobName]
  );
};
